import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import UsageDialog from './UsageDialog'
import { getAISubscription, warnIfLowAfterRun, type AISubscription } from '@/lib/ai'
import { setLowBalanceTopUp } from '@/lib/lowBalance'
import { BillingContext, type PricingTab } from './billingContext'

/**
 * Holds the agent's plan for everything that shows it (the sidebar, the
 * Usage page, the pricing page), and sends any screen to the pricing page
 * with `openPricing()`.
 */
export const BillingProvider = ({ children }: { children: ReactNode }) => {
    const navigate = useNavigate()
    const [subscription, setSubscription] = useState<AISubscription | null | undefined>(undefined)

    const refreshSubscription = useCallback(async () => {
        try {
            const { subscription: next } = await getAISubscription()
            setSubscription(next)
            return next
        } catch {
            // The plan label is a nicety; leave it as it was.
            setSubscription((current) => current ?? null)
            return null
        }
    }, [])

    useEffect(() => {
        refreshSubscription()
    }, [refreshSubscription])

    // Coming back to the app with little left also warns, once per level.
    useEffect(() => {
        warnIfLowAfterRun()
    }, [])

    // Bumped whenever the balance may have changed (a plan change, a
    // purchase), so whatever shows it loads it again.
    const [balanceVersion, setBalanceVersion] = useState(0)
    const refreshBalance = useCallback(() => setBalanceVersion((v) => v + 1), [])

    // Usage is a dialog. Coming back from Stripe (?checkout=…) opens it, so
    // the purchase can be confirmed there.
    const [usageOpen, setUsageOpen] = useState(false)

    // Going to pricing closes Usage, which would otherwise stay over the page.
    const openPricing = useCallback(
        (tab: PricingTab = 'plans') => {
            setUsageOpen(false)
            navigate(tab === 'credits' ? '/pricing?tab=credits' : '/pricing')
        },
        [navigate],
    )
    // The low-balance warning's "Top up" navigates inside the app, so an
    // unsaved form behind it survives.
    useEffect(() => {
        setLowBalanceTopUp(() => openPricing('credits'))
        return () => setLowBalanceTopUp(null)
    }, [openPricing])
    const openUsage = useCallback(() => setUsageOpen(true), [])
    const location = useLocation()
    useEffect(() => {
        const params = new URLSearchParams(location.search)
        if (params.has('checkout') || params.has('usage')) setUsageOpen(true)
    }, [location.search])

    const value = useMemo(
        () => ({ subscription, refreshSubscription, openPricing, setSubscription, openUsage, balanceVersion, refreshBalance }),
        [subscription, refreshSubscription, openPricing, openUsage, balanceVersion, refreshBalance],
    )

    return (
        <BillingContext.Provider value={value}>
            {children}
            <UsageDialog open={usageOpen} onOpenChange={setUsageOpen} />
        </BillingContext.Provider>
    )
}
